import { DateTime } from '@ksyos/date-time';
import { OrderedMap, Record } from 'immutable';
import { v4 as uuidv4 } from 'uuid';
import type { IAllergy } from '../../../../../interfaces/allergy';
import { fetchJson } from '../../../../../../store/fetch';
import { ProfessionalSummaryDataAction } from '../../../../professionalSummaryData/ProfessionalSummaryDataSection';

const allergyRecordType = Record({
    id: null,
    patientId: null,
    codeSystem: null,
    displayName: null,
    valueCode: null,
    comment: null,
    statusCode: null,
    startDate: null,
    endDate: null,
    action: ProfessionalSummaryDataAction.add,
});

export class AllergyRecord extends allergyRecordType {
    public readonly id: string;
    public readonly patientId: string;
    public readonly codeSystem: string;
    public readonly displayName: string;
    public readonly valueCode: string;
    public readonly comment: string;
    public readonly statusCode: string;
    public readonly startDate: string | null;
    public readonly endDate: string | null;
    public readonly action: ProfessionalSummaryDataAction;

    constructor(values?: any) {
        super(values);
    }
}

export type Allergies = OrderedMap<string, AllergyRecord>;

export async function loadRequestAllergies(
    patient: { authorization?: { canSeePatientAllergies?: boolean }; id: string },
    requestId?: string
) {
    let allergies = await loadPatientAllergies(patient, Boolean(requestId));

    if (requestId) {
        const requestAllergiesResponse = await fetchJson(`/api/requests/${requestId}/requestAllergies`, 'GET');
        for (const requestAllergy of requestAllergiesResponse.json.items) {
            const key = makeAllergyKey(requestAllergy);
            if (allergies.has(key)) {
                allergies = allergies.setIn([key, 'action'], ProfessionalSummaryDataAction.pick);
            } else {
                allergies = allergies.set(uuidv4(), new AllergyRecord(requestAllergy));
            }
        }
    }

    return allergies;
}

export async function loadProcessActivityAllergies(
    patient: { authorization?: { canSeePatientAllergies?: boolean }; id: string },
    processActivityId?: string
) {
    let allergies = await loadPatientAllergies(patient);

    if (processActivityId) {
        const processActivityAllergiesResponse = await fetchJson(
            `/api/processActivities/${processActivityId}/allergies`,
            'GET'
        );

        for (const processActivityAllergy of processActivityAllergiesResponse.json.items) {
            const key = makeAllergyKey(processActivityAllergy);
            if (allergies.has(key)) {
                allergies = allergies.setIn([key, 'action'], ProfessionalSummaryDataAction.pick);
            } else {
                allergies = allergies.set(uuidv4(), new AllergyRecord(processActivityAllergy));
            }
        }
    }

    return allergies;
}

async function loadPatientAllergies(
    patient: { authorization?: { canSeePatientAllergies?: boolean }; id: string },
    isEditAllergies?: boolean
) {
    let allergies: Allergies = OrderedMap();
    if (patient.authorization && patient.authorization.canSeePatientAllergies) {
        const patientAllergiesResponse = await fetchJson(`/api/patients/${patient.id}/allergies`, 'GET');
        for (const patientAllergy of patientAllergiesResponse.json.items) {
            allergies = allergies.set(
                makeAllergyKey(patientAllergy),
                new AllergyRecord({ ...patientAllergy, action: ProfessionalSummaryDataAction.skip })
            );
        }
    }

    return allergies;
}

export function isCurrentAllergy(allergy: { endDate: string | null; startDate: string | null }) {
    if (allergy.startDate && DateTime.now().isBefore(allergy.startDate)) {
        return false;
    }
    if (allergy.endDate && DateTime.now().isAfter(allergy.endDate)) {
        return false;
    }

    return true;
}

function makeAllergyKey(allergy: IAllergy) {
    return (
        allergy.codeSystem +
        '\u0000' +
        allergy.comment +
        '\u0000' +
        allergy.displayName +
        '\u0000' +
        allergy.statusCode +
        '\u0000' +
        allergy.patient +
        '\u0000' +
        allergy.valueCode +
        '\u0000' +
        allergy.startDate +
        '\u0000' +
        allergy.endDate
    );
}

export function filterAndPrepareAllergiesForSaving(allergies?: Allergies) {
    if (!allergies) {
        return [];
    }

    return allergies
        .valueSeq()
        .filter((allergy) => {
            if (!allergy) {
                return false;
            } else if (allergy.action === ProfessionalSummaryDataAction.add) {
                return !!allergy.displayName;
            } else {
                return allergy.action === ProfessionalSummaryDataAction.pick;
            }
        })
        .toJS();
}
